import { HotTable } from '@handsontable/react-wrapper';
import { Button, Dialog, DialogTitle, DialogContent, Table, TableHead, TableRow, TableCell, TableBody, IconButton, Tabs, Tab, Box } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import AddIcon from '@mui/icons-material/Add';
import dayjs from 'dayjs';
import Handsontable from 'handsontable';
import { CellChange, CellValue, ChangeSource, RangeType } from 'handsontable/common';
import "handsontable/dist/handsontable.full.min.css";
import { DropdownEditor } from 'handsontable/editors';
import { ColumnSettings } from 'handsontable/settings';
import 'handsontable/styles/ht-theme-main.min.css';
import { toNumber } from 'lodash';
import { useSnackbar } from 'notistack';
import { useEffect, useMemo, useRef, useState } from 'react';
import { CommodityManagementApi } from '../../../../../Apis/Apis';
import { AllocationModel, CounterpartyModel, ReportType, CustomerReportModel, PositionModel, CommodityModel, UpdateAllocationGroupRequest, UpdateAllocationRequest, AuditTrailModel, Currency, GetCustomerDataResponse, GetCustomerSettingsResponse, ContractDate, PortfolioModel, LocationModel, Unit, UpdateDealTicketRequest, UpdatePositionModel, PositionPricingStrategyModel, PositionType, DeliveryModel } from '../../../../../Generated/Commodity-Management-Api';
import { months, monthToNumber } from '../../../../../Helpers';
import { useLoadingState } from '../../../../../Hooks';
import { RootState, useApplicationSelector } from '../../../../../Redux/ReduxStore';
import { StoneXDivider, StoneXRow } from '../../../../StoneX';
import StoneXMainPage from '../../../../StoneX/StoneXMainPage/StoneXMainPage';
import { StoneXAutocomplete, StoneXButton, StoneXContractMonthDropdown, StoneXLabeledItem, StoneXNumber, StoneXSelect, StoneXTextField } from '../../../../StoneXMui';
import PositionTable from '../../Positions/ViewPositionsPage/PositionTable';
import { PositionReportColumn, PositionReportConfiguration, PositionReportUserOverride } from '../../Positions/ViewPositionsPage/Types';
import { deepMerge } from '../../../../../Helpers/ObjectHelper';
import { PositionCalculations } from '../../../../../Helpers/PositionCalculations'; 
import './UpdateAllocation.scss'

interface UpdateAllocationProps {
    allocationModel?: AllocationModel;
    allPositions?: PositionModel[];
    onCancel?: () => void;
    onSave?: (AllocationId: number) => void;
    onDelete?: (AllocationId: number) => void;
}

export default function UpdateAllcoation(props: UpdateAllocationProps) {
    const { onCancel, onSave, onDelete, allPositions } = props;
    const [isSaving, setIsSaving] = useState<boolean>(false);
    const { enqueueSnackbar } = useSnackbar();
    const allocationLoadingState = useLoadingState();
    const auditLoadingState = useLoadingState();
    const [allocationModel, setAllocationModel] = useState<AllocationModel | undefined>(props.allocationModel);
    const [allocationName, setAllocationName] = useState<string>('');
    const [allocationNameError, setAllocationNameError] = useState<string>();

    const customerReports = useApplicationSelector((state: RootState): CustomerReportModel[] => state.commodityManagement.customerReports);
    const customerReport = customerReports.find((x: any) => x.reportType === ReportType.Position)!;
    const reportConfiguration = useMemo(() => JSON.parse(customerReport.configuration!) as PositionReportConfiguration, [customerReport]);
    const userOverrides = useMemo(() => customerReport.userOverrides ? JSON.parse(customerReport.userOverrides) as PositionReportUserOverride : undefined, [customerReport]);
    const reportSettings = useMemo(() => deepMerge(reportConfiguration, userOverrides), [reportConfiguration, userOverrides]);
    const columnsToShow = useMemo(getColumnsToShow, [reportSettings]);
    const [editDealTicketModalOpen, setEditDealTicketModalOpen] = useState(false);
    const [editDealTicketId, setEditDealTicketId] = useState<number | undefined>();
    const [validationModalOpen, setValidationModalOpen] = useState(false);
    const [validationDealTicketId, setDealTicketValidationId] = useState<number | undefined>();
    const [positions, setPositions] = useState<PositionModel[]>([]);
    const [auditTrailModalIsOpen, setAuditTrailModalIsOpen] = useState(false);
    const [AuditTrailModels, setAuditTrialChangeEvents] = useState<AuditTrailModel[]>([]);
    const [budgetStartMonth, setBudgetStartMonth] = useState<ContractDate | null>(null);
    const [newVolume, setNewVolume] = useState<number | undefined>();

    const [counterpartyError, setCounterpartyError] = useState<string>();
    const [selectedCounterparty, setSelectedCounterparty] = useState<CounterpartyModel | null>(null);

    const [portfolioError, setPortfolioError] = useState<string>();
    const [selectedPortfolio, setSelectedPortfolio] = useState<PortfolioModel | null>(null);

    const [locationError, setLocationError] = useState<string>();
    const [selectedLocation, setSelectedLocation] = useState<LocationModel | null>(null);

    const [commodityError, setCommodityError] = useState<string>();
    const [selectedCommodity, setSelectedCommodity] = useState<CommodityModel | null>(null);

    const customerData = useApplicationSelector((state: RootState): GetCustomerDataResponse => state.commodityManagement.customerData);
    const customerSettings = useApplicationSelector((state: RootState): GetCustomerSettingsResponse => state.commodityManagement.customerSettings);

    const [totalVolume, setTotalVolume] = useState<number>(0);

    // State for allocation modal
    const [allocateModalOpen, setAllocateModalOpen] = useState(false);
    const [allocateModalPositions, setAllocateModalPositions] = useState<PositionModel[]>([]);
    const [allocateModalMonth, setAllocateModalMonth] = useState<string>("");

    // State for new month dialog
    const [addMonthDialogOpen, setAddMonthDialogOpen] = useState(false);
    const [newMonthKey, setNewMonthKey] = useState('');
    const [newMonthError, setNewMonthError] = useState<string | undefined>(undefined);
    const [monthKeys, setMonthKeys] = useState<string[]>([]);

    useEffect(() => {
        if (!allocationModel) {
            setAllocationModel({
                allocationGroupId: 0,
                siteGuid: '',
                allocationName: '',
                isComplete: false,
                monthlyVolume: "",
                positions: [],
                budgets:[],
                location: {} as LocationModel, 
                counterparty: {} as CounterpartyModel, 
                allocations: [],
                commodity: {} as CommodityModel,
                unit: {} as Unit,
                deliveries: []
            });
        }
    }, []);

    function getColumnsToShow() {
        return reportSettings.columns?.active;
    }

    function editDealTicket(dealTicketId?: number) {
        // This will work as add deal ticket if dealTicketId is undefined
        setEditDealTicketId(dealTicketId);
        setEditDealTicketModalOpen(true);
    }
    function setPositionValidation(dealTicketId: number) {
        setValidationModalOpen(true);
        setDealTicketValidationId(dealTicketId);
    }
    function onValidationUpdated(dealTicketId: number, validatedPositionIds: number[]) {
        setPositions(previous => previous.map(q => {
            if (q.dealTicketId === dealTicketId) {
                q.isValidated = !!validatedPositionIds.find((r) => r === q.positionId);
            }
            return q;
        }))
    }
    function viewAuditTrail(positionId: number) {
        setAuditTrailModalIsOpen(true);
        CommodityManagementApi.getPositionAuditTrail(auditLoadingState.setLoadingState, positionId.toString()).then(q => {
            setAuditTrialChangeEvents(q.data.rows ?? []);
        });
    }

    function saveAllocationGroup() {
        const allocationGroup = {
            allocationGroupId: allocationModel?.allocationGroupId ?? 0,
            locationId: selectedLocation?.locationId,
            counterpartyId: selectedCounterparty?.counterpartyId,
            commodityId: selectedCommodity?.commodityId,
            allocationName: allocationName,
            comments: allocationModel?.comments,
            monthlyVolume: allocationModel?.monthlyVolume
        } as UpdateAllocationGroupRequest;

        setIsSaving(true);
        CommodityManagementApi.addAllocationGroup(null, allocationGroup)
            .then(async res => {
                
                onSave && await onSave(res.data.allocationGroupId!);
                setIsSaving(false);
                const message = allocationGroup
                    ? 'Dealticket was successfully updated'
                    : 'Dealticket was successfully added';
                enqueueSnackbar(message, { variant: 'success' });
            })
            .catch(res => {
                enqueueSnackbar('An error occured while saving the allocation', { variant: 'error' });
                setIsSaving(false);
            });
        
    }

    function allocateVolume(positionId: number,volume: number) {
        const allocation = {
            allocationId: 0,
            allocationGroupId: allocationModel?.allocationGroupId ?? 0,
            positionId: positionId,
            volume: volume
        } as UpdateAllocationRequest;

        setIsSaving(true);
        CommodityManagementApi.addAllocation(null, allocation)
            .then(async res => {

                onSave && await onSave(res.data.allocationId!);
                setIsSaving(false);
                const message = allocation
                    ? 'Allocation was successfully updated'
                    : 'Allocation was successfully added';
                enqueueSnackbar(message, { variant: 'success' });
            })
            .catch(res => {
                enqueueSnackbar('An error occured while saving the allocation', { variant: 'error' });
                setIsSaving(false);
            });

    }

    // Parse monthlyVolume safely
    let monthlyVolume: Record<string, number> = {};
    if (allocationModel?.monthlyVolume) {
        try {
            monthlyVolume = typeof allocationModel.monthlyVolume === "string"
                ? JSON.parse(allocationModel.monthlyVolume)
                : allocationModel.monthlyVolume;
        } catch {
            monthlyVolume = {};
        }
    }

    // Helper to add a new month key
    function handleAddMonth() {
        if (!budgetStartMonth?.year || !budgetStartMonth?.month) {
            setNewMonthError('Please select a valid month');
            return;
        }
        const paddedMonth = budgetStartMonth.month.toString().padStart(2, '0');
        const trimmed = `${budgetStartMonth.year}-${paddedMonth}`;

        if (monthlyVolume.hasOwnProperty(trimmed)) {
            setNewMonthError('Month already exists');
            return;
        }
        // Add to monthlyVolume and allocationModel
        // Add updatedMonthlyVolume to the end of monthlyVolume
        const updatedMonthlyVolume = { ...monthlyVolume, [trimmed]: newVolume || 0 };
        // Reorder so the new month is at the end
        const reorderedMonthlyVolume: Record<string, number> = {};
        Object.keys(monthlyVolume).forEach(key => {
            reorderedMonthlyVolume[key] = monthlyVolume[key];
        });
        reorderedMonthlyVolume[trimmed] = newVolume || 0;

        allocationModel!.monthlyVolume = JSON.stringify(reorderedMonthlyVolume);
        setMonthKeys(Object.keys(reorderedMonthlyVolume));
        
        setNewMonthKey('');
        setNewMonthError(undefined);
        setAddMonthDialogOpen(false);

        // Don't setSelectedTab here!
    }

    // Tab state
    //setMonthKeys(Object.keys(monthlyVolume));
    const [selectedTab, setSelectedTab] = useState(0);

    // Calculate totalVolume when monthlyVolume changes
    useEffect(() => {
        if (!allocationModel?.monthlyVolume) {
            setTotalVolume(0);
            return;
        }
        let monthlyVolume: Record<string, number> = {};
        try {
            monthlyVolume = typeof allocationModel.monthlyVolume === "string"
                ? JSON.parse(allocationModel.monthlyVolume)
                : allocationModel.monthlyVolume;
        } catch {
            monthlyVolume = {};
        }
        const sum = Object.values(monthlyVolume).reduce((acc, val) => acc + (Number(val) || 0), 0);
        setTotalVolume(sum);
    }, [allocationModel?.monthlyVolume]); // Re-run when allocationModel changes

    useEffect(() => {
        setAllocationName(allocationModel?.allocationName ?? '');
        setSelectedCounterparty(allocationModel?.counterparty!);
        setSelectedLocation(allocationModel?.location!);
        setSelectedCommodity(allocationModel?.commodity!);
        setMonthKeys(Object.keys(monthlyVolume));
    }, []); 

    function handleAllocateClick(month: string, positionsToAllocate: PositionModel[]) {
        const currentMonthNumber = Number((month || '').split('-')[1]);
        const currentYearNumber = Number((month || '').split('-')[0]);
        
        setAllocateModalPositions(allPositions!);
        setAllocateModalMonth(month);
        setAllocateModalOpen(true);
    }

    function renderAllocateModal() {
        const [allocateVolumes, setAllocateVolumes] = useState<Record<number, string>>({});
        return (
            <Dialog open={allocateModalOpen} onClose={() => setAllocateModalOpen(false)} maxWidth="md" fullWidth>
                <DialogTitle>
                    Positions to Allocate for {allocateModalMonth ? dayjs(allocateModalMonth + '-01').format('MMM-YYYY') : ''}
                    <IconButton
                        aria-label="close"
                        onClick={() => setAllocateModalOpen(false)}
                        sx={{ position: 'absolute', right: 8, top: 8 }}
                    >
                        <CloseIcon />
                    </IconButton>
                </DialogTitle>
                <DialogContent>
                    <Table size="small">
                        <TableHead>
                            <TableRow>
                                <TableCell>Budget Date</TableCell>
                                <TableCell>Contract Date</TableCell>
                                <TableCell>Position Type</TableCell>
                                <TableCell>Side</TableCell>
                                <TableCell>Price</TableCell>
                                <TableCell>Volume</TableCell>
                                <TableCell>Volume</TableCell>
                                <TableCell>Allocate</TableCell>
                                <TableCell></TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {allocateModalPositions.map((p, idx) => (
                                <TableRow key={p.positionId ?? idx}>
                                    <TableCell>{p.budgetDate?.label ?? ""}</TableCell>
                                    <TableCell>{p.contractDate?.label ?? ""}</TableCell>
                                    <TableCell>{p.positionType ?? ""}</TableCell>
                                    <TableCell>{p.side ?? ""}</TableCell>
                                    <TableCell>{p.price ?? ""}</TableCell>
                                    <TableCell>{p.volume ?? ""}</TableCell>
                                    <TableCell>{p.unit ?? ""}</TableCell>
                                    <TableCell>
                                        <StoneXTextField
                                            label="Volume"
                                            name={p.positionId?.toString()}
                                            value={allocateVolumes[p.positionId] || ""}
                                            onChange={e => setAllocateVolumes({
                                                ...allocateVolumes,
                                                [p.positionId!]: e.target.value
                                            })}
                                        />
                                    </TableCell>
                                    <TableCell>
                                        <Button
                                            variant="contained"
                                            size="small"
                                            onClick={() => allocateVolume(p.positionId, Number(allocateVolumes[p.positionId])) }
                                        >
                                            Allocate
                                        </Button>
                                    </TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                </DialogContent>
            </Dialog>
        );
    }

    function rowDetail(month: string, volume: number) {

        const currentMonthNumber = Number((month || '').split('-')[1]);
        const currentYearNumber = Number((month || '').split('-')[0]);

        const futurePositions = allocationModel!.positions.filter(p => {
            return p.budgetDate.month === currentMonthNumber && p.budgetDate.year === currentYearNumber &&
                (p.positionType === PositionType.Future || p.positionType === PositionType.Option);
        });

        const physicalPositions = allocationModel!.positions.filter(p => {
            return p.budgetDate.month === currentMonthNumber && p.budgetDate.year === currentYearNumber &&
                (p.positionType != PositionType.Future && p.positionType != PositionType.Option);
        });

        var positionsToAllocate = positions.filter(p => {
            return p.budgetDate.month === currentMonthNumber && p.budgetDate.year === currentYearNumber;
        });

        const openMarketPrice = allocationModel!.budgets.find(b => b.budgetDate.month === currentMonthNumber && b.budgetDate.year === currentYearNumber)?.marketPrice || 0;

        const physicalPositionsVolume = physicalPositions.filter(f => f.positionType != "Basis").reduce((acc, pos) => acc + (pos.volume || 0), 0);
        const openVolume = volume - physicalPositionsVolume;
        const futuresPositionsCalculations = PositionCalculations.weightedAveragePrice(futurePositions, openVolume < 0 ? physicalPositionsVolume : openVolume);
        const longFuturesPositionsCalculations = PositionCalculations.weightedAveragePrice(futurePositions.filter(f => f.side == "Buy"), openVolume < 0 ? physicalPositionsVolume : openVolume);
        const shortFuturesPositionsCalculations = PositionCalculations.weightedAveragePrice(futurePositions.filter(f => f.side == "Sell"), openVolume < 0 ? physicalPositionsVolume : openVolume);
        const physicalPositionsCalculations = PositionCalculations.weightedAveragePrice(physicalPositions.filter(f => f.positionType != "Basis"), openVolume < 0 ? physicalPositionsVolume : openVolume);
        const basisPositionsCalculations = PositionCalculations.weightedAveragePrice(physicalPositions.filter(f => f.positionType == "Basis"), openVolume < 0 ? physicalPositionsVolume : openVolume);

        return (
            <Box sx={{ p: 2 }}>
                <div style={{ display: 'flex', alignItems: 'center', width: '100%' }}>
                    <span style={{ minWidth: 120, fontWeight: 600 }}>{dayjs(month + '-01').format('MMM-YYYY')}</span>
                    
                    <div style={{ flex: 1 }} />
                    <StoneXButton
                        variant="contained"
                        size="small"
                        onClick={() => handleAllocateClick(month, positionsToAllocate)}
                    >
                        Allocate
                    </StoneXButton>
                </div>
                <div style={{ display: 'flex', alignItems: 'flex-end', marginLeft: 8, minWidth: 180, width: 'fit-content' }}>
                    <div style={{ display: 'flex', flexDirection: 'column', minWidth: 300, marginRight: 15 }}>
                        <div className='divHeader' >
                            <span className='spanHeader' >Monthly Volume</span>
                        </div>
                        <div className='divLabel' style={{backgroundColor:'cornflowerblue'} }>
                            <span className='spanLabel'>Volume:</span>
                            <span className='spanValue'><StoneXNumber number={volume} /></span>
                        </div>
                        <div className='divLabel' style={{ backgroundColor: 'cornflowerblue' }}>
                            <span className='spanLabel'>Futures Volume:</span>
                            <span className='spanValue'><StoneXNumber number={futuresPositionsCalculations.totalVolume} /></span>
                        </div>
                        <div className='divLabel' style={{ backgroundColor: 'cornflowerblue' }}>
                            <span className='spanLabel'>Physical Volume:</span>
                            <span className='spanValue'><StoneXNumber number={physicalPositionsCalculations.totalVolume} /></span>
                        </div>
                        <div className='divLabel' style={{ backgroundColor: 'cornflowerblue' }}>
                            <span className='spanLabel'>Open Volume:</span>
                            <span className='spanValue'><StoneXNumber number={volume - futuresPositionsCalculations.totalVolume - physicalPositionsCalculations.totalVolume} /></span>
                        </div>
                        <div className='divLabel' style={{ backgroundColor: 'cornflowerblue' }}>
                            <span className='spanLabel'>-</span>
                        </div>
                    </div>
                    <div style={{ display: 'flex', flexDirection: 'column', minWidth: 300, marginRight: 8, backgroundColor: 'lightskyblue' }}>
                        <div className='divHeader'>
                            <span className='spanHeader'>Physcial Purchase</span>
                        </div>
                        <div className='divLabel' style={{ backgroundColor: 'dodgerblue' }}>
                            <span className='spanLabel'>Quantity:</span>
                            <span className='spanValue'><StoneXNumber number={physicalPositionsCalculations.totalVolume} /></span>
                        </div>
                        <div className='divLabel' style={{ backgroundColor: 'dodgerblue' }}>
                            <span className='spanLabel'>Physical Price:</span>
                            <span className='spanValue'><StoneXNumber number={physicalPositionsCalculations.weightedAveragePrice} currency={Currency.Usd} decimals={2} /></span>
                        </div>
                        <div className='divLabel' style={{ backgroundColor: 'dodgerblue' }}>
                            <span className='spanLabel'>Basis Price:</span>
                            <span className='spanValue'><StoneXNumber number={basisPositionsCalculations.weightedAveragePrice} currency={Currency.Usd} decimals={2} /></span>
                        </div>
                        <div className='divLabel' style={{ backgroundColor: 'dodgerblue' }}>
                            <span className='spanLabel'>Fixed Price:</span>
                            <span className='spanValue'><StoneXNumber number={physicalPositionsCalculations.weightedAveragePrice + basisPositionsCalculations.weightedAveragePrice} currency={Currency.Usd} decimals={2} /></span>
                        </div>
                        <div className='divLabel' style={{ backgroundColor: 'dodgerblue' }}>
                            <span className='spanLabel'>Total Spend:</span>
                            <span className='spanValue'><StoneXNumber number={(physicalPositionsCalculations.weightedAveragePrice + basisPositionsCalculations.weightedAveragePrice) * physicalPositionsCalculations.totalVolume} currency={Currency.Usd} decimals={2} /></span>
                        </div>
                    </div>
                    <div style={{ display: 'flex', flexDirection: 'column', minWidth: 300, marginRight: 15, backgroundColor: 'lightcyan' }}>
                        <div className='divHeader'>
                            <span className='spanHeader'>Financial Hedge</span>
                        </div>
                        <div className='divLabel' style={{ backgroundColor: 'lightskyblue' }}>
                            <span className='spanLabel'>Quantity:</span>
                            <span className='spanValue'><StoneXNumber number={longFuturesPositionsCalculations.totalVolume} /></span>
                        </div>
                        <div className='divLabel' style={{ backgroundColor: 'lightskyblue' }} >
                            <span className='spanLabel'>Long Futures Price:</span>
                            <span className='spanValue'><StoneXNumber number={longFuturesPositionsCalculations.weightedAveragePrice} currency={Currency.Usd} decimals={2} /></span>
                        </div>
                        <div className='divLabel' style={{ backgroundColor: 'lightskyblue'}} >
                            <span className='spanLabel'>Short Futures Price:</span>
                            <span className='spanValue'><StoneXNumber number={shortFuturesPositionsCalculations.weightedAveragePrice} currency={Currency.Usd} decimals={2} /></span>
                        </div>
                        <div className='divLabel' style={{ backgroundColor: 'lightskyblue'}} >
                            <span className='spanLabel'>Gain/Loss Price:</span>
                            <span className='spanValue'><StoneXNumber number={shortFuturesPositionsCalculations.weightedAveragePrice - longFuturesPositionsCalculations.weightedAveragePrice} currency={Currency.Usd} decimals={2} /></span>
                        </div>
                        <div className='divLabel' style={{ backgroundColor: 'lightskyblue'}} >
                            <span className='spanLabel'>Gain/Loss Value:</span>
                            <span className='spanValue'><StoneXNumber number={futuresPositionsCalculations.mtmValue} currency={Currency.Usd} decimals={2} /></span>
                        </div>
                    </div>
                    <div style={{ display: 'flex', flexDirection: 'column', minWidth: 300, marginRight: 15, backgroundColor: 'paleturquoise' }}>
                        <div className='divHeader'>
                            <span className='spanHeader'>Total</span>
                        </div>
                        <div className='divLabel' style={{ backgroundColor: 'lightsteelblue' }}>
                            <span className='spanLabel' >Quantity:</span>
                            <span className='spanValue'><StoneXNumber number={physicalPositionsCalculations.totalVolume + (openVolume < 0 ? 0 : openVolume) } /></span>
                        </div>
                        <div className='divLabel' style={{ backgroundColor: 'lightsteelblue' }}>
                            <span className='spanLabel'>Blended Physical Price:</span>
                            <span className='spanValue'><StoneXNumber number={(physicalPositionsCalculations.totalVolume > 0 ? physicalPositionsCalculations.blendedPrice + basisPositionsCalculations.blendedPrice : openMarketPrice)} currency={Currency.Usd} decimals={2} /></span>
                        </div>
                        <div className='divLabel' style={{ backgroundColor: 'lightsteelblue' }}>
                            <span className='spanLabel' >Net Price:</span>
                            <span className='spanValue'><StoneXNumber number={(physicalPositionsCalculations.positionValue + basisPositionsCalculations.positionValue - futuresPositionsCalculations.mtmValue) / (physicalPositionsCalculations.totalVolume + openVolume)} currency={Currency.Usd} decimals={2} /></span>
                        </div>
                        <div className='divLabel' style={{ backgroundColor: 'lightsteelblue' }}>
                            <span className='spanLabel'>Net Cost:</span>
                            <span className='spanValue'><StoneXNumber number={(physicalPositionsCalculations.totalVolume > 0 ? physicalPositionsCalculations.positionValue + basisPositionsCalculations.positionValue : openMarketPrice * futuresPositionsCalculations.totalVolume) - futuresPositionsCalculations.mtmValue} currency={Currency.Usd} decimals={2} /></span>
                        </div>
                        <div className='divLabel' style={{ backgroundColor: 'lightsteelblue' }}>
                            <span className='spanLabel' >-</span>
                        </div>
                    </div>


                </div>

                <StoneXDivider />
                <span style={{ minWidth: 120, fontWeight: 600 }}>Futures</span>
                <br />
                <div style={{ width: '100%' }}>
                    {futurePositions.length > 0 && (
                        <PositionTable
                            customerSettings={customerSettings}
                            positions={futurePositions}
                            columnsToShow={columnsToShow}
                            onEditPosition={editDealTicket}
                            onSetPositionValidation={setPositionValidation}
                            onViewAuditTrail={viewAuditTrail}
                        />
                    )}
                </div>
                <StoneXDivider />
                <span style={{ minWidth: 120, fontWeight: 600 }}>Physical</span>
                <br />
                <div style={{ width: '100%' }}>
                    {physicalPositions.length > 0 && (
                        <PositionTable
                            customerSettings={customerSettings}
                            positions={physicalPositions}
                            columnsToShow={columnsToShow}
                            onEditPosition={editDealTicket}
                            onSetPositionValidation={setPositionValidation}
                            onViewAuditTrail={viewAuditTrail}
                        />
                    )}
                </div>
                <StoneXDivider />
            </Box>
        );
    }

    return (
        <StoneXMainPage isLoading={allocationLoadingState.isLoading()}>
            <StoneXRow align='start'>

                <StoneXTextField
                    type="text"
                    label="Allocation Name"
                    value={allocationName}
                    onChange={(event) => setAllocationName(event.target.value)}
                    showRequired
                    error={allocationNameError}
                    disabled={isSaving}
                />

                {customerSettings.positionsByCounterparty && (
                    <StoneXAutocomplete
                        options={customerData?.counterparties ?? []}
                        getOptionLabel={(q) => q.counterpartyName!}
                        value={selectedCounterparty}
                        onChange={(e: any, counterparty: CounterpartyModel | null) => setSelectedCounterparty(counterparty)}
                        //isOptionEqualToValue={(a, b) => a.counterpartyId == b.counterpartyId}
                        label="Counterparty"
                        showRequired={customerSettings.positionsByCounterparty}
                        error={counterpartyError}
                        disabled={isSaving}
                    />
                )}

                {customerSettings.positionsByLocation && (
                    <StoneXAutocomplete
                        options={customerData?.locations ?? []}
                        getOptionLabel={(q) => q.locationName!}
                        value={selectedLocation}
                        onChange={(e: any, location: LocationModel | null) => setSelectedLocation(location)}
                        //isOptionEqualToValue={(a, b) => a.locationId == b.locationId}
                        label="Location"
                        showRequired={customerSettings.positionsByLocation}
                        error={locationError}
                        disabled={isSaving}
                    />
                )}
                {customerSettings.positionsByPortfolio && (
                    <StoneXAutocomplete
                        options={customerData?.portfolios ?? []}
                        getOptionLabel={(q) => q.portfolioName!}
                        value={selectedPortfolio}
                        onChange={(e: any, portfolio: PortfolioModel | null) => setSelectedPortfolio(portfolio)}
                        isOptionEqualToValue={(a, b) => a.portfolioId == b.portfolioId}
                        label="Portfolio"
                        showRequired={customerSettings.positionsByPortfolio}
                        error={portfolioError}
                        disabled={isSaving}
                    />
                )}

                <StoneXAutocomplete
                    options={customerData?.commodities ?? []}
                    getOptionLabel={(q) => q.commodityName}
                    value={selectedCommodity}
                    onChange={(e: any, commodity: CommodityModel | null) => setSelectedCommodity(commodity)}
                    isOptionEqualToValue={(a, b) => a.commodityId == b.commodityId}
                    label="Commodity"
                    showRequired
                    error={commodityError}
                    disabled={isSaving}
                />

            </StoneXRow>
            <StoneXRow align="center" style={{ marginBottom: 8, flexDirection: 'column', alignItems: 'stretch' }}>
                <span style={{ minWidth: 120, fontWeight: 600 }}>Total Volume: <StoneXNumber number={totalVolume} /></span>
            </StoneXRow>
            <StoneXDivider />

            {/* Tabs for months */}
            <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 2 }}>
                <Tabs
                    value={monthKeys.length === 0 ? "add" : selectedTab}
                    onChange={(_, newValue) => {
                        if (newValue === "add" || newValue === monthKeys.length) {
                            setAddMonthDialogOpen(true);
                        } else {
                            setSelectedTab(newValue);
                        }
                    }}
                    variant="scrollable"
                    scrollButtons="auto"
                >
                    {monthKeys.map((month, idx) => (
                        <Tab key={month} label={dayjs(month + '-01').format('MMM-YYYY')} value={idx} />
                    ))}
                    <Tab
                        icon={<AddIcon />}
                        label="Add Month"
                        value="add"
                        onClick={monthKeys.length === 0 ? () => setAddMonthDialogOpen(true) : undefined}
                    />
                </Tabs>
            </Box>

            {/* New Month Dialog */}
            <Dialog open={addMonthDialogOpen} onClose={() => setAddMonthDialogOpen(false)}>
                <DialogTitle>Add New Month</DialogTitle>
                <DialogContent>
                    <StoneXContractMonthDropdown value={budgetStartMonth} label="Start Month" onChange={setBudgetStartMonth} width='small' />
                    <StoneXTextField
                        label="Volume"
                        value={newVolume}
                        onChange={(event) => setNewVolume(event.target.value)}
                        //error={!!newMonthError}
                        //helperText={newMonthError}
                        //autoFocus
                    />
                    <div style={{ marginTop: 16, textAlign: 'right' }}>
                        <Button onClick={() => setAddMonthDialogOpen(false)}>Cancel</Button>
                        <Button variant="contained" onClick={handleAddMonth} style={{ marginLeft: 8 }}>Add</Button>
                    </div>
                </DialogContent>
            </Dialog>

            {/* Tab content */}
            {monthKeys.length > 0 && rowDetail(monthKeys[selectedTab], monthlyVolume[monthKeys[selectedTab]])}

            {renderAllocateModal()}

            <StoneXRow align="center">
                

                <div className="pull-right nested-row-item">
                    <StoneXButton variant='outlined' onClick={onCancel} >Cancel</StoneXButton>
                    <StoneXButton
                        onClick={saveAllocationGroup}
                        disabled={false}
                        isWorking={isSaving}>{allocationModel ? "Save" : "Add"}
                    </StoneXButton>
                </div>
            </StoneXRow>
        </StoneXMainPage>
    );

}